import React, { useState, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Info, Trash2, Loader2 } from 'lucide-react';

const ReportIssueModal = memo(({
  open,
  onOpenChange,
  reportTarget,
  existingReportTypes,
  hasExistingReports,
  onSubmit,
  onDeleteReport,
  submitting,
}) => {
  const { t } = useTranslation('products');

  // Local state for inputs - prevents parent re-renders
  const [issues, setIssues] = useState({
    bad_url: false,
    wrong_rrp: false,
    wrong_ean: false,
    wrong_vendor_code: false,
  });
  const [rrpValue, setRrpValue] = useState('');
  const [eanValue, setEanValue] = useState('');
  const [vendorCodeValue, setVendorCodeValue] = useState('');

  // Reset form when modal opens with new target
  useEffect(() => {
    if (open && reportTarget) {
      // Pre-fill with existing values if available
      const row = reportTarget.row;
      setIssues({
        bad_url: existingReportTypes?.bad_url || false,
        wrong_rrp: existingReportTypes?.wrong_rrp || false,
        wrong_ean: existingReportTypes?.wrong_ean || false,
        wrong_vendor_code: existingReportTypes?.wrong_vendor_code || false,
      });
      setRrpValue(existingReportTypes?.wrong_rrp_value || row?.v_compareAtPrice || row?.compare_at_price || '');
      setEanValue(existingReportTypes?.wrong_ean_value || row?.v_ean || '');
      setVendorCodeValue(existingReportTypes?.wrong_vendor_code_value || row?.v_vendor_code || '');
    }
  }, [open, reportTarget, existingReportTypes]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setIssues({ bad_url: false, wrong_rrp: false, wrong_ean: false, wrong_vendor_code: false });
      setRrpValue('');
      setEanValue('');
      setVendorCodeValue('');
    }
  }, [open]);

  const handleSubmit = useCallback(() => {
    onSubmit({
      issues,
      rrpValue,
      eanValue,
      vendorCodeValue,
    });
  }, [issues, rrpValue, eanValue, vendorCodeValue, onSubmit]);

  const handleDelete = useCallback((issueType) => {
    setIssues(prev => ({ ...prev, [issueType]: false }));
    onDeleteReport(issueType);
  }, [onDeleteReport]);

  const row = reportTarget?.row;
  const currentRrp = row?.v_compareAtPrice || row?.compare_at_price;
  const currentEan = row?.v_ean;
  const currentVendorCode = row?.v_vendor_code;

  // Validation
  const isRrpValid = !issues.wrong_rrp || (
    rrpValue &&
    parseFloat(rrpValue) > 0 &&
    parseFloat(rrpValue) !== parseFloat(currentRrp || 0)
  );
  const isEanValid = !issues.wrong_ean || (
    eanValue &&
    eanValue.trim().length >= 8 &&
    eanValue.trim() !== (currentEan || '').trim()
  );
  const isVendorCodeValid = !issues.wrong_vendor_code || (
    vendorCodeValue &&
    vendorCodeValue.trim() !== (currentVendorCode || '').trim()
  );
  const hasAnyIssue = issues.bad_url || issues.wrong_rrp || issues.wrong_ean || issues.wrong_vendor_code;
  const isFormValid = hasAnyIssue && isRrpValid && isEanValid && isVendorCodeValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{t('report.title')}</DialogTitle>
        </DialogHeader>

        {hasExistingReports && (
          <div className="px-6 pt-2">
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-900 dark:text-blue-100">
                {t('report.existingReportsInfo')}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-3 px-6 pb-3">
          {/* Bad URL - only on Price Monitoring tab */}
          {reportTarget?.tab === 'priceMonitoring' && (
            <div
              className="flex flex-col border rounded-lg p-3 bg-muted/30 cursor-pointer"
              onClick={() => setIssues(prev => ({ ...prev, bad_url: !prev.bad_url }))}
            >
              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium w-full" onClick={(e) => e.preventDefault()}>
                <input
                  type="checkbox"
                  checked={issues.bad_url}
                  onChange={() => {}}
                  className="pointer-events-none"
                />
                <span className="flex-1">{t('report.badUrl')}</span>
                {existingReportTypes?.bad_url && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete('bad_url');
                    }}
                    className="ml-auto p-1 hover:bg-destructive/10 rounded text-destructive"
                    title={t('report.cancelReport')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </label>
            </div>
          )}

          {/* Wrong RRP */}
          <div
            className="flex flex-col space-y-2 border rounded-lg p-3 bg-muted/30 cursor-pointer"
            onClick={(e) => {
              if (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') return;
              setIssues(prev => ({ ...prev, wrong_rrp: !prev.wrong_rrp }));
            }}
          >
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium w-full" onClick={(e) => e.preventDefault()}>
              <input
                type="checkbox"
                checked={issues.wrong_rrp}
                onChange={() => {}}
                className="pointer-events-none"
              />
              <span className="flex-1">
                {currentRrp ? t('report.wrongRrpWithPrice', { price: parseFloat(currentRrp).toFixed(2) }) : t('report.wrongRrp')}
              </span>
              {existingReportTypes?.wrong_rrp && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete('wrong_rrp');
                  }}
                  className="ml-auto p-1 hover:bg-destructive/10 rounded text-destructive"
                  title={t('report.cancelReport')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </label>
            {issues.wrong_rrp && (
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <Label className="text-sm font-medium">{t('report.newRrp')}</Label>
                <Input
                  type="number"
                  placeholder={t('report.newRrpPlaceholder')}
                  value={rrpValue}
                  onChange={(e) => setRrpValue(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Wrong EAN */}
          <div
            className="flex flex-col space-y-2 border rounded-lg p-3 bg-muted/30 cursor-pointer"
            onClick={(e) => {
              if (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') return;
              setIssues(prev => ({ ...prev, wrong_ean: !prev.wrong_ean }));
            }}
          >
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium w-full" onClick={(e) => e.preventDefault()}>
              <input
                type="checkbox"
                checked={issues.wrong_ean}
                onChange={() => {}}
                className="pointer-events-none"
              />
              <span className="flex-1">
                {currentEan ? t('report.wrongEanWithValue', { ean: currentEan }) : t('report.wrongEan')}
              </span>
              {existingReportTypes?.wrong_ean && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete('wrong_ean');
                  }}
                  className="ml-auto p-1 hover:bg-destructive/10 rounded text-destructive"
                  title={t('report.cancelReport')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </label>
            {issues.wrong_ean && (
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <Label className="text-sm font-medium">{t('report.newEan')}</Label>
                <Input
                  type="text"
                  placeholder={t('report.newEanPlaceholder')}
                  value={eanValue}
                  onChange={(e) => setEanValue(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Wrong Vendor Code */}
          <div
            className="flex flex-col space-y-2 border rounded-lg p-3 bg-muted/30 cursor-pointer"
            onClick={(e) => {
              if (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') return;
              setIssues(prev => ({ ...prev, wrong_vendor_code: !prev.wrong_vendor_code }));
            }}
          >
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium w-full" onClick={(e) => e.preventDefault()}>
              <input
                type="checkbox"
                checked={issues.wrong_vendor_code}
                onChange={() => {}}
                className="pointer-events-none"
              />
              <span className="flex-1">
                {currentVendorCode ? t('report.wrongVendorCodeWithValue', { code: currentVendorCode }) : t('report.wrongVendorCode')}
              </span>
              {existingReportTypes?.wrong_vendor_code && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete('wrong_vendor_code');
                  }}
                  className="ml-auto p-1 hover:bg-destructive/10 rounded text-destructive"
                  title={t('report.cancelReport')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </label>
            {issues.wrong_vendor_code && (
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <Label className="text-sm font-medium">{t('report.newVendorCode')}</Label>
                <Input
                  type="text"
                  placeholder={t('report.newVendorCodePlaceholder')}
                  value={vendorCodeValue}
                  onChange={(e) => setVendorCodeValue(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('report.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !isFormValid}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('report.submitting')}
              </>
            ) : (
              hasExistingReports ? t('report.update') : t('report.save')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

ReportIssueModal.displayName = 'ReportIssueModal';

export default ReportIssueModal;
